import * as Sentry from '@sentry/capacitor';
import isPermittedNetworkConnected from '../../../sync/network/network';
import { SyncAborted } from '../../../sync/sync';
import { NonReportedError } from '../../error/errorApi';
import { graphql } from '../../../utils/api/apiUtils';
import { _setting } from '../../../utils/state/model/implementations/ImplementationFactory';
import { DexieSetting } from '../../../utils/state/model/implementations/dexie/implementationModel/dexieObjects';

export const updateOrganizationSettingsFromServer = async () => {
  isPermittedNetworkConnected().then((isConnected) => {
    if (!isConnected) {
      console.log('No network connection, so we are not updating the organization settings');
      return;
    }

    getSettingsFromServer()
      .then(async (settings) => {
        await updateSettings(settings);

        document.dispatchEvent(new CustomEvent('orgSettingsUpdated', { detail: settings }));
      })
      .catch((error) => {
        if (error instanceof SyncAborted) {
          console.error(
            'We recieved notice that the sync was aborted, so we are not updating the organization settings'
          );
          return;
        }

        console.error('Error when updating organization settings from server: ', error);

        const expectedNetworkFailure = error instanceof NonReportedError;

        /*
          Capture the original error so we can root cause OTUVY-FRONTLINE-3.
          Expected network failures are reported (tagged) for now; once we confirm
          they account for all occurrences, this capture can be limited to
          unexpected failures only.
        */
        Sentry.captureException(error, {
          tags: {
            feature: 'orgSettingsSync',
            expected_network_failure: String(expectedNetworkFailure),
          },
        });

        if (expectedNetworkFailure) {
          // Offline blips are normal for this background refresh; the next sync will retry.
          return;
        }

        throw new Error('Failed to update organization settings from server', { cause: error });
      });
  });
};

const getSettingsFromServer = async (): Promise<DexieSetting[]> => {
  const query = `
        query getOrgSettingsQuery {
            getOrganizationSettings {
                key
                value
            }
        }
    `;
  const settings = await graphql<DexieSetting[]>(query);
  return settings;
};

const updateSettings = async (settings: DexieSetting[]) => {
  await _setting.setSettings(settings);
};
